// Free-form note ⇄ Markdown with YAML front matter.
//
// Export: rich-text note (Quill HTML) → a .md file with `---` front matter
// (title/metadata) and a Markdown body, the format docs-as-code tools ingest.
// Import is a trust boundary: the file is user-supplied, so the Markdown is
// rendered to HTML and then DOMPurify-sanitized before it touches the editor.

import yaml from "js-yaml";
import TurndownService from "turndown";
import { marked } from "marked";
import DOMPurify from "dompurify";

const turndown = new TurndownService({
  headingStyle: "atx",
  bulletListMarker: "-",
  codeBlockStyle: "fenced",
});

// Tags marked can emit from Markdown; everything else is stripped on import.
const ALLOWED_TAGS = [
  "p", "br", "hr", "strong", "em", "b", "i", "u", "s", "del",
  "h1", "h2", "h3", "h4", "h5", "h6",
  "ul", "ol", "li", "a", "blockquote", "pre", "code", "span",
  "table", "thead", "tbody", "tr", "th", "td", "img",
];
const ALLOWED_ATTR = ["href", "title", "src", "alt", "target", "rel"];

/** Rich-text note → Markdown string with YAML front matter. */
export function noteToMarkdown(name: string, html: string): string {
  const front = yaml.dump({ name: (name || "Untitled Note").trim() }, { lineWidth: -1, noRefs: true });
  const body = turndown.turndown(html || "").trim();
  return `---\n${front}---\n\n${body}\n`;
}

/** Markdown (optionally with front matter) → { name, html }. Sanitizes the body. */
export function markdownToNote(md: string): { name: string; html: string } {
  const text = String(md || "");
  let name = "";
  let body = text;

  // Split leading `---\n ... \n---` front matter if present.
  const fm = text.match(/^﻿?---\r?\n([\s\S]*?)\r?\n---\r?\n?([\s\S]*)$/);
  if (fm) {
    try {
      // JSON_SCHEMA permits only plain JSON types (no custom/JS tags) — no code
      // execution path. (js-yaml 4's default load is already safe; this is explicit.)
      const meta = yaml.load(fm[1], { schema: yaml.JSON_SCHEMA }) as { name?: string; title?: string } | null;
      name = String(meta?.name || meta?.title || "").trim();
    } catch {
      // Malformed front matter: ignore it, keep the whole file as body.
    }
    if (name) body = fm[2];
  }

  const rawHtml = marked.parse(body, { async: false }) as string;
  const clean = DOMPurify.sanitize(rawHtml, { ALLOWED_TAGS, ALLOWED_ATTR });
  // Name is plain text only.
  const safeName =
    DOMPurify.sanitize(name || "Imported Note", { ALLOWED_TAGS: [] }).trim() || "Imported Note";

  return { name: safeName, html: clean };
}
